import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCycle } from "../../context/CycleContext";
import { getTeamGoalSheets } from "../../services/goalApi";
import { getLookups } from "../../services/employeeApi";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Loader, ErrorState, DataTable, PersonCell, StatusBadge, Tabs, Button, RemindButton, EmptyState,
  OrgFilters, countByDepartment, groupByDepartment,
} from "../../components/common";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue } from "../../utils/orgFilters";
import { REMINDER_TASK_TYPES, SHEET_STATUS, MAX_GOALS_PER_YEAR } from "../../utils/constants";
import { formatDate } from "../../utils/format";
import { formatDateTime } from "../../utils/format";

const api = { getTeamGoalSheets, getLookups };

export default function GoalApprovalsPage() {
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const departmentId = singleValue(org.filters.departmentId); // PM-18: one department → one group
  const { data: allSheets, error, reload } = useAsync(() => api.getTeamGoalSheets(cycleId), [cycleId], { skip: !cycleId });
  const lookups = useAsync(() => api.getLookups(), []);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!allSheets) return <Loader />;

  const counts = countByDepartment(allSheets);
  const departments = (lookups.data?.departments || []).filter((d) => counts[d.id]);
  const data = allSheets.filter((r) => org.matches(r.employee));
  const pending = data.filter((r) => r.sheetStatus === SHEET_STATUS.PENDING);
  const owing = data.filter((r) => [SHEET_STATUS.NOT_STARTED, SHEET_STATUS.DRAFT, SHEET_STATUS.CHANGES_REQUESTED].includes(r.sheetStatus));
  const approved = data.filter((r) => r.sheetStatus === SHEET_STATUS.APPROVED);
  const goalSettingEnds = cycle?.stages?.find((st) => st.key === "goalSetting")?.endDate;
  const reminderText = (r) =>
    r?.sheetStatus === SHEET_STATUS.CHANGES_REQUESTED
      ? "I have asked for a few changes on your goals. Please update and resubmit them."
      : `Please add your goals (weights totalling 100%) and submit them for approval${goalSettingEnds ? ` by ${formatDate(goalSettingEnds)}` : ""}.`;
  const rows = tab === "pending" ? pending : tab === "owing" ? owing : approved;
  return (
    <div className="page">
      <PageHeader
        title="Goal approvals"
        subtitle={`Goal sheets your team submitted for ${cycle?.name}.`}
        actions={
          <div className="page-filters">
          <OrgFilters org={org} />
          {owing.length > 1 && (
            <RemindButton
              size="md"
              label={`Remind all not submitted (${owing.length})`}
              recipients={owing.map((r) => ({ id: r.employee.id, name: r.employee.name }))}
              taskType={REMINDER_TASK_TYPES.GOAL_SETTING}
              defaultMessage={reminderText(null)}
            />
          )}
          </div>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "pending", label: "Waiting for you", count: pending.length },
          { key: "owing", label: "Not submitted yet", count: owing.length },
          { key: "all", label: "Approved goals", count: data.length },
        ]}
      />
      {(departmentId ? [{ id: departmentId, name: departments.find((d) => d.id === departmentId)?.name || "Department", rows }] : groupByDepartment(rows)).map((g, i, all) => (
      <Card key={g.id} title={all.length > 1 || departmentId ? g.name : undefined} subtitle={all.length > 1 || departmentId ? `${g.rows.length} ${g.rows.length === 1 ? "sheet" : "sheets"}` : undefined} padded={false}>
        <DataTable
          rows={g.rows}
          rowKey={(r) => r.employee.id}
          onRowClick={(r) => navigate(`/team/goal-approvals/${r.employee.id}`)}
          emptyTitle={tab === "pending" ? "No goals waiting" : tab === "owing" ? "Everyone has submitted" : "No team members"}
          emptyText={tab === "pending" ? "When someone submits goals, they'll show up here." : undefined}
          columns={[
            { key: "name", label: "Employee", render: (r) => <PersonCell name={r.employee.name} meta={`${r.employee.designationName}, ${r.employee.departmentName}`} /> },
            { key: "goals", label: "Goals", align: "right", render: (r) => <span title={`Maximum ${MAX_GOALS_PER_YEAR} goals per year`}>{r.goalCount} / {MAX_GOALS_PER_YEAR}</span> },
            { key: "weight", label: "Goal %", align: "right", render: (r) => `${r.totalWeightage}%` },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.sheetStatus} /> },
            { key: "last", label: "Last activity", render: (r) => (r.lastAction ? `${r.lastAction.action}, ${formatDateTime(r.lastAction.at)}` : "—") },
            {
              key: "act", label: "", align: "right",
              render: (r) => r.sheetStatus === SHEET_STATUS.PENDING
                ? <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/team/goal-approvals/${r.employee.id}`); }}>Review</Button>
                : (
                  <span className="row-actions">
                    {owing.includes(r) && (
                      <RemindButton
                        recipients={[{ id: r.employee.id, name: r.employee.name }]}
                        taskType={REMINDER_TASK_TYPES.GOAL_SETTING}
                        defaultMessage={reminderText(r)}
                      />
                    )}
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/team/goal-approvals/${r.employee.id}`); }}>View</Button>
                  </span>
                ),
            },
          ]}
        />
      </Card>
      ))}
      {!rows.length && !org.active && (
        <Card>
          <EmptyState
            title={tab === "pending" ? "No goals waiting" : tab === "owing" ? "Everyone has submitted" : "No team members"}
            text={tab === "pending" ? "When someone submits goals, they'll show up here." : undefined}
          />
        </Card>
      )}
    </div>
  );
}
